import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { adminApi } from '../../lib/api';
import type { AdminUser } from '../../lib/api';
import type { UserRole } from '@asset-mgmt/shared';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';

const ROLE_COLORS: Record<UserRole, string> = {
  admin:  'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  editor: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  viewer: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

export default function AdminUserDetail() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const toast = useToast();

  const [user, setUser] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editRole, setEditRole] = useState<UserRole>('viewer');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  const isSelf = user?.id === currentUser?.id;

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    adminApi.getUser(userId)
      .then((u) => {
        setUser(u);
        setEditName(u.displayName);
        setEditEmail(u.email);
        setEditRole(u.role);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [userId]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    if (!userId) return;
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await adminApi.updateUser(userId, {
        displayName: editName,
        email: editEmail,
        ...(isSelf ? {} : { role: editRole }),
      });
      setUser(updated);
      toast.success('User updated.');
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    }
    setSaving(false);
  }

  async function handleForceLogout() {
    if (!userId || !confirm('Revoke all sessions for this user?')) return;
    setLoggingOut(true);
    try {
      const { revoked } = await adminApi.forceLogout(userId);
      toast.success(`Revoked ${revoked} session(s).`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed');
    }
    setLoggingOut(false);
  }

  async function handleDelete() {
    if (!userId || isSelf || !user) return;
    if (!confirm(`Permanently delete ${user.email} and all their data? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await adminApi.deleteUser(userId);
      navigate('/admin/users');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed');
      setDeleting(false);
    }
  }

  if (loading) return <div className="p-6 text-gray-400">Loading…</div>;
  if (!user) return <div className="p-6 text-red-500">User not found</div>;

  const inp = 'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 dark:focus:ring-primary-900/30';

  return (
    <div className="p-4 sm:p-6">
      <div className="mb-6 flex items-center gap-3">
        <Link to="/admin/users" className="text-sm text-gray-400 hover:text-primary-600">← Users</Link>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{user.displayName}</h1>
        <span className={`rounded px-2 py-0.5 text-xs font-medium capitalize ${ROLE_COLORS[user.role]}`}>{user.role}</span>
      </div>

      <div className="max-w-lg space-y-6">
        {/* Edit form */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 shadow-sm">
          <h2 className="mb-4 text-base font-semibold text-gray-800 dark:text-gray-200">Edit User</h2>
          <form onSubmit={(e) => void handleSave(e)} className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Display Name</label>
              <input value={editName} onChange={(e) => setEditName(e.target.value)} required className={inp} />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
              <input type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} required className={inp} />
            </div>
            {!isSelf && (
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Role</label>
                <select value={editRole} onChange={(e) => setEditRole(e.target.value as UserRole)} className={inp}>
                  <option value="viewer">viewer</option>
                  <option value="editor">editor</option>
                  <option value="admin">admin</option>
                </select>
              </div>
            )}
            <div className="space-y-1 text-sm text-gray-500 dark:text-gray-400">
              <p><span className="font-medium text-gray-700 dark:text-gray-300">Joined:</span> {user.createdAt.slice(0, 10)}</p>
              <p><span className="font-medium text-gray-700 dark:text-gray-300">Telegram linked:</span> {user.settings.telegramLinked ? 'Yes' : 'No'}</p>
            </div>
            {saveError && <p className="text-xs text-red-500">{saveError}</p>}
            <button type="submit" disabled={saving} className="rounded-lg bg-primary-600 px-5 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </form>
        </div>

        {/* Danger zone */}
        {!isSelf && (
          <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/30 p-5">
            <h2 className="mb-4 text-base font-semibold text-red-700 dark:text-red-400">Danger Zone</h2>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Force Logout</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Revoke all active sessions for this user.</p>
                </div>
                <button onClick={() => void handleForceLogout()} disabled={loggingOut} className="rounded-lg border border-amber-300 dark:border-amber-600 px-3 py-1.5 text-xs font-medium text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30 disabled:opacity-50">
                  {loggingOut ? '…' : 'Force Logout'}
                </button>
              </div>
              <div className="flex items-center justify-between border-t border-red-100 dark:border-red-800 pt-3">
                <div>
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Delete User</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Permanently removes user and all their data.</p>
                </div>
                <button onClick={() => void handleDelete()} disabled={deleting} className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50">
                  {deleting ? '…' : 'Delete User'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
